<template>
    <div
        :class="[classes, $attrs.class]"
        :style="[$attrs.style, rowVars]"
    >
        <textarea
            ref="control"
            v-model="model"
            :class="element('control')"
            :name="name"
            :rows="rows"
            :maxlength="maxlength"
            :placeholder="placeholder"
            :readonly="readonly || undefined"
            v-bind="{ ...controlAttrs, ...fallthroughAttrs }"
        />

        <div
            v-if="showsCounter || $slots.hint"
            :class="element('meta')"
        >
            <span
                v-if="$slots.hint"
                :class="element('hint')"
            >
                <slot name="hint" />
            </span>

            <span
                v-if="showsCounter"
                :class="element('counter')"
                aria-hidden="true"
            >
                <slot
                    name="counter"
                    :length="length"
                    :maxlength="maxlength"
                    :remaining="remaining"
                >{{ length }} / {{ maxlength }}</slot>
            </span>
        </div>

        <!-- Kept mounted while empty: a live region inserted at announce time is missed by
             several screen readers. Written only near the limit, and debounced. -->
        <p
            v-if="showsCounter"
            class="k-visually-hidden"
            :class="[element('status')]"
            aria-live="polite"
        >
            {{ status }}
        </p>
    </div>
</template>

<script setup lang="ts">
import type { KTextareaProps } from './types'
import useBem from '@yeeroen/use-bem'
import { computed, onMounted, onScopeDispose, ref, useAttrs, useTemplateRef, watch } from 'vue'

import { useFieldControl } from '../field/use-field-control'

defineOptions({ name: 'KTextarea', inheritAttrs: false })

const props = withDefaults(defineProps<KTextareaProps>(), {
    rows: 3,
    autosize: false,
    maxRows: 0,
    showCount: false,
    resize: 'vertical',
    fullWidth: true,
    readonly: false,
    // Left undefined on purpose — see the note in KInput.
    disabled: undefined,
    required: undefined,
    invalid: undefined,
    size: undefined,
})

defineSlots<{
    counter?: (props: { length: number, maxlength?: number, remaining: number }) => unknown
    hint?: () => unknown
}>()

// Announcing every keystroke makes the field unusable, so the live region only speaks once
// the remaining count is actionable, and only after typing pauses.
const COUNT_THRESHOLD = 20
const COUNT_DEBOUNCE = 1000

const model = defineModel<string>({ default: '' })

const attrs = useAttrs()
const { block, element, modifier } = useBem('k-textarea')
const { controlAttrs, invalid, disabled, size } = useFieldControl(props)
const control = useTemplateRef<HTMLTextAreaElement>('control')
const status = ref('')

// `inheritAttrs: false` sweeps `class` and `style` into `$attrs` as well, but they belong
// on the wrapper.
const fallthroughAttrs = computed(() => {
    const { class: _class, style: _style, ...rest } = attrs

    return rest
})

const resizeMode = computed(() => props.autosize ? 'none' : props.resize)
const length = computed(() => (model.value ?? '').length)
const remaining = computed(() => (props.maxlength ?? 0) - length.value)
const showsCounter = computed(() => Boolean(props.maxlength) && props.showCount)
const rowVars = computed(() => ({
    '--textarea-min-rows': props.rows,
    '--textarea-max-rows': props.maxRows || undefined,
}))
const classes = computed(() => block(
    modifier(size.value),
    modifier('invalid', invalid.value),
    modifier('disabled', disabled.value),
    modifier('readonly', props.readonly),
    modifier('full-width', props.fullWidth),
    modifier('autosize', props.autosize),
    modifier('max-rows', props.autosize && props.maxRows > 0),
    modifier(`resize-${resizeMode.value}`),
    modifier('at-limit', showsCounter.value && remaining.value === 0),
))

// Decided once, never both: the fallback writes an inline `height`, which `field-sizing`
// then has to fight, and the box lands a line off. Guarded for SSR — `CSS` is undefined in
// the nitro bundle, so the server always emits the non-autosized markup and onMounted
// fixes it up.
const supportsFieldSizing = typeof CSS !== 'undefined' && CSS.supports('field-sizing', 'content')

function resize() {
    const el = control.value

    if (supportsFieldSizing || !el) {
        return
    }

    if (!props.autosize) {
        el.style.height = ''

        return
    }

    // `scrollHeight` never reports less than the current box, so without the reset the
    // control grows and never shrinks. The reset scrolls the page in some engines.
    const { scrollTop } = document.documentElement

    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
    document.documentElement.scrollTop = scrollTop
}

if (!supportsFieldSizing) {
    // Watching the model, not `@input`: a prefill from an API, a form reset or a parent
    // writing v-model fires no input event, and the box keeps the previous value's height.
    watch([model, () => props.autosize], resize, { flush: 'post' })
    onMounted(resize)
}

let statusTimer: ReturnType<typeof setTimeout> | undefined

watch(remaining, (value) => {
    clearTimeout(statusTimer)

    if (!showsCounter.value || value > COUNT_THRESHOLD) {
        status.value = ''

        return
    }

    statusTimer = setTimeout(() => {
        status.value = `${value} characters remaining`
    }, COUNT_DEBOUNCE)
})

onScopeDispose(() => clearTimeout(statusTimer))

// For a KTextarea revealed by a dialog, tab panel or accordion: a hidden textarea measures
// `scrollHeight === 0`, so the container calls this when it opens.
defineExpose({ resize })
</script>

<style lang="scss">
@use './k-textarea.scss';
</style>
